import * as XLSX from 'xlsx';
import type { Transaction, TransactionType } from '../types';

export interface CardInfo {
  identifier: string;
  type: 'digital' | 'titular' | 'adicional';
}

export interface ParseResult {
  transactions: Omit<
    Transaction,
    'id' | 'categoryId' | 'importedAt' | 'importBatchId'
  >[];
  cardInfo: CardInfo[];
  errors: string[];
}

interface RawRow {
  date: string;
  description: string;
  charge: number | null;
  credit: number | null;
  balance: number | string | null;
}

const CARD_TYPE_MAP: Record<string, CardInfo['type']> = {
  digital: 'digital',
  titular: 'titular',
  adicional: 'adicional',
};

const INSTALLMENT_PATTERN = /^(\d{2})\s+DE\s+(\d{2})\s+(.+)$/i;

export function generateInstallmentGroupId(
  merchantName: string,
  cardIdentifier: string,
  totalInstallments: number
): string {
  const normalizedMerchant = merchantName
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 20);
  return `inst-${normalizedMerchant}-${cardIdentifier}-${totalInstallments}`;
}

function detectCardType(text: string): CardInfo['type'] {
  const lowerText = text.toLowerCase();
  for (const [key, type] of Object.entries(CARD_TYPE_MAP)) {
    if (lowerText.includes(key)) {
      return type;
    }
  }
  return 'titular';
}

function extractCardIdentifier(text: string): string | null {
  const match = text.match(/\*(\d{4,5})$/);
  return match ? match[1] : null;
}

function parseDate(dateStr: string): string {
  const parts = dateStr.split('/');
  if (parts.length !== 3) {
    return '';
  }
  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

function parseAmount(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  const cleaned = String(value).replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

function parseBalance(
  value: number | string | null | undefined
): number | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (value === 'En tránsito') {
    return undefined;
  }
  if (typeof value === 'number') {
    return value;
  }
  const cleaned = String(value).replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? undefined : num;
}

function isCardHeaderRow(row: unknown[]): boolean {
  if (!row || row.length === 0) return false;
  const firstCell = String(row[0] || '').trim();
  return (
    firstCell.includes('Digital') ||
    firstCell.includes('Titular') ||
    firstCell.includes('Adicional')
  );
}

function isDataRow(row: unknown[]): boolean {
  if (!row || row.length < 2) return false;
  const firstCell = String(row[0] || '').trim();
  return /^\d{2}\/\d{2}\/\d{4}$/.test(firstCell);
}

function parseInstallmentInfo(
  description: string
): { current: number; total: number; baseDescription: string } | null {
  const match = description.match(INSTALLMENT_PATTERN);
  if (!match) return null;
  return {
    current: parseInt(match[1], 10),
    total: parseInt(match[2], 10),
    baseDescription: match[3].trim(),
  };
}

export function parseWorkbookData(data: unknown[][]): ParseResult {
  const errors: string[] = [];
  const cardInfo: CardInfo[] = [];
  const transactions: ParseResult['transactions'] = [];

  let currentCard: CardInfo | null = null;

  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (!row || !Array.isArray(row) || row.length === 0) continue;

    if (isCardHeaderRow(row)) {
      const headerText = String(row[0]).trim();
      const identifier = extractCardIdentifier(headerText);
      const type = detectCardType(headerText);

      if (identifier) {
        currentCard = { identifier, type };
        if (!cardInfo.some((c) => c.identifier === identifier)) {
          cardInfo.push(currentCard);
        }
      }
      continue;
    }

    if (isDataRow(row) && currentCard) {
      try {
        const rawRow: RawRow = {
          date: String(row[0]),
          description: String(row[1] || ''),
          charge: parseAmount(row[2] as number | string | null),
          credit: parseAmount(row[3] as number | string | null),
          balance: row[4] as number | string | null,
        };

        const isoDate = parseDate(rawRow.date);
        if (!isoDate) {
          errors.push(`Invalid date at row ${i + 1}: ${rawRow.date}`);
          continue;
        }

        const amount = rawRow.charge ?? -(rawRow.credit ?? 0);
        if (amount === 0 && rawRow.charge === null && rawRow.credit === null) {
          errors.push(`No amount found at row ${i + 1}`);
          continue;
        }

        // Determine transaction type based on CARGO (charge) vs ABONO (credit)
        const transactionType: TransactionType =
          rawRow.charge !== null ? 'expense' : 'income';

        const installmentInfo = parseInstallmentInfo(rawRow.description);
        const description = installmentInfo
          ? installmentInfo.baseDescription
          : rawRow.description;

        const trimmedDescription = description.trim();
        const groupId = installmentInfo
          ? generateInstallmentGroupId(
              trimmedDescription,
              currentCard.identifier,
              installmentInfo.total
            )
          : '';

        const transaction: ParseResult['transactions'][number] = {
          date: isoDate,
          description: trimmedDescription,
          amount,
          type: transactionType,
          balance: parseBalance(rawRow.balance),
          cardIdentifier: currentCard.identifier,
          cardType: currentCard.type,
          isInstallment: !!installmentInfo,
          ...(installmentInfo && {
            installmentInfo: {
              current: installmentInfo.current,
              total: installmentInfo.total,
              groupId,
            },
          }),
        };

        transactions.push(transaction);
      } catch (rowError) {
        errors.push(
          `Error parsing row ${i + 1}: ${rowError instanceof Error ? rowError.message : 'Unknown error'}`
        );
      }
    }
  }

  if (cardInfo.length === 0) {
    errors.push('No card information found in the file');
  }

  if (transactions.length === 0) {
    errors.push('No transactions found in the file');
  }

  return { transactions, cardInfo, errors };
}

export async function parseBankExport(file: File): Promise<ParseResult> {
  const errors: string[] = [];
  const cardInfo: CardInfo[] = [];
  const transactions: ParseResult['transactions'] = [];

  try {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    if (workbook.SheetNames.length === 0) {
      errors.push('No sheets found in the workbook');
      return { transactions, cardInfo, errors };
    }

    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const data = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1 });

    return parseWorkbookData(data);
  } catch (error) {
    errors.push(
      `Failed to parse file: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }

  return { transactions, cardInfo, errors };
}

export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}
